'use client'

import { GRADE_COLORS } from '@/lib/radar/scoring'
import {
  categoryControlLabel,
  summariseCategories,
  type CategorySummary,
} from '@/lib/radar/categories'
import type { ToolKey, ToolState } from '@/types/radar'
import { GradeChip, InstrumentLabel, MeterRow, Panel } from './ui'

/**
 * The five category summary cards.
 *
 * Presentation only — every number comes from `lib/radar/categories.ts`, which
 * excludes withheld / failed / unscored tools rather than averaging them in as
 * zero. Nothing here computes a score, and nothing here feeds the primary
 * Radar score.
 *
 * A category with no usable score renders `--` with an empty meter track. That
 * is a deliberate difference from a real 0, which renders as a number with a
 * grade-F bar.
 */
export function CategoryGrid({
  tools,
  onCategoryClick,
}: {
  /**
   * Partial, not full: `tools` is only forwarded to `summariseCategories`,
   * which already accepts a partial map and treats a missing key exactly like
   * an unscored one. Requiring every key was an over-constraint that blocked
   * callers holding a subset (see components/home/RadarDemo).
   */
  tools: Partial<Record<ToolKey, ToolState>>
  /** Jumps to the tool table filtered to this category. */
  onCategoryClick?: (categoryKey: string) => void
}) {
  const summaries = summariseCategories(tools)

  return (
    <section
      aria-label='Category summaries'
      className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'
    >
      {summaries.map(summary => (
        <CategoryCard
          key={summary.key}
          summary={summary}
          onClick={onCategoryClick}
        />
      ))}
    </section>
  )
}

function CategoryCard({
  summary,
  onClick,
}: {
  summary: CategorySummary
  onClick?: (categoryKey: string) => void
}) {
  const hasScore = summary.score !== null
  const interactive = !!onClick

  const body = (
    <>
      <div className='flex items-center justify-between gap-2'>
        <InstrumentLabel tone='secondary'>{summary.label}</InstrumentLabel>
        {hasScore ? (
          <GradeChip grade={summary.grade} />
        ) : (
          <span className='instrument-label text-instrument leading-none'>
            --
          </span>
        )}
      </div>

      <div
        className='font-display text-[31px] font-semibold leading-none tracking-[-0.02em] tabular-nums'
        style={
          hasScore
            ? { color: GRADE_COLORS[summary.grade!] ?? undefined }
            : { color: 'var(--pm-text-muted)' }
        }
      >
        {hasScore ? summary.score : '--'}
      </div>

      <div className='flex flex-col gap-2.5'>
        {summary.members.map(member => (
          <MeterRow key={member.key} label={member.name} value={member.score} />
        ))}
      </div>

      {!hasScore && (
        <span className='block text-xs text-instrument'>
          No gradeable result in this category yet.
        </span>
      )}
    </>
  )

  const shared = 'flex w-full flex-col gap-4 p-5 text-left transition-colors'

  if (!interactive) {
    return (
      <Panel as='div' className='flex flex-col'>
        <div className={shared}>{body}</div>
      </Panel>
    )
  }

  return (
    <Panel as='div' className='flex flex-col'>
      <button
        type='button'
        onClick={() => onClick?.(summary.key)}
        aria-label={categoryControlLabel(summary)}
        className={`${shared} hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset`}
      >
        {body}
      </button>
    </Panel>
  )
}
